/**
 * TideBreak's public network contract.
 *
 * The server is authoritative. Clients send only intent (settings edits,
 * ready state and controller input); positions, scores and finish times are
 * always computed by the server.
 */

export const PROTOCOL_VERSION = 1 as const;

export const PARK_THEMES = [
  "sunfruit-bay",
  "glacier-gulp",
] as const;
export type ParkTheme = (typeof PARK_THEMES)[number];

export const COURSE_LENGTHS = ["short", "medium", "long"] as const;
export type CourseLength = (typeof COURSE_LENGTHS)[number];

export const DIFFICULTIES = ["relaxed", "tricky", "feral"] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export const BOT_MODES = ["off", "fixed", "fill"] as const;
export type BotMode = (typeof BOT_MODES)[number];

export const BOT_DIFFICULTIES = ["easy", "medium", "hard"] as const;
export type BotDifficulty = (typeof BOT_DIFFICULTIES)[number];

export const COLLISION_MODES = ["off", "light", "full"] as const;
export type CollisionMode = (typeof COLLISION_MODES)[number];

export const SHORTCUT_MODES = ["guided", "normal", "extreme"] as const;
export type ShortcutMode = (typeof SHORTCUT_MODES)[number];

export const POOL_SHAPES = [
  "random",
  "circle",
  "rectangle",
  "square",
  "star",
  "ring",
  "crescent",
  "tiny",
] as const;
export type PoolShape = (typeof POOL_SHAPES)[number];

export const SKIN_IDS = [
  "axolotl",
  "capybara",
  "shark",
  "duck",
  "crocodile",
  "red-panda",
  "pigeon",
  "frog",
  "otter",
  "tiger",
  "sloth",
  "unicorn",
  "statesman",
  "vip-splash",
] as const;

export interface GameSettings {
  theme: ParkTheme;
  courseLength: CourseLength;
  difficulty: Difficulty;
  seed: number;
  botMode: BotMode;
  botCount: number;
  botDifficulty: BotDifficulty;
  maxRacers: number;
  collisions: CollisionMode;
  shortcuts: ShortcutMode;
  powerups: boolean;
  poolShape: PoolShape;
  bestOf: 1 | 3 | 5;
  teams: boolean;
  chaos: number;
}

export type GameSettingsPatch = Partial<GameSettings>;
export type RoomPhase = "lobby" | "countdown" | "racing" | "results";
export type AirborneMode = "none" | "hop" | "shortcut" | "edge" | "finale" | "recovery";

export interface PlayerRaceState {
  progress: number;
  lane: number;
  speed: number;
  airborne: boolean;
  /** The authoritative trajectory currently controlling the rider. */
  airborneMode: AirborneMode;
  /** Normalised 0..1 time through the current jump/drop arc. */
  airborneProgress: number;
  /** Authoritative height above the slide/drop trajectory. */
  verticalOffset: number;
  /** True while a rider is taking a route beyond the flume walls. */
  offSlide: boolean;
  /** Side of the active shortcut, or 0 on the main route. */
  shortcutSide: -1 | 0 | 1;
  /** Signed off-slide displacement; 0 is centre and +/-1 is a flume wall. */
  glideOffset: number;
  /** Normalised course progress where the current free flight began. */
  launchProgress: number;
  /** Signed lateral coordinate at launch; the client preserves that origin. */
  launchGlideOffset: number;
  /** Player-controlled signed lateral flight velocity. */
  glideVelocity: number;
  /** Forward travel since launch measured in normalised course lengths. */
  flightProgress: number;
  /** True during the short non-eliminating rescue after a missed re-entry. */
  recovering: boolean;
  /** Normalised 0..1 progress through the current rescue. */
  recoveryProgress: number;
  /** Normalised 0..1 progress through the final pool drop. */
  finaleProgress: number;
  boost: number;
  boosting: boolean;
  stunts: number;
  shortcuts: number;
  edgeLaunches: number;
  reentries: number;
  wipeouts: number;
  boostPickups: number;
  obstacleHits: number;
  collisions: number;
  dropperScore: number;
  /** Null before the final drop, then whether the selected pool was hit. */
  poolHit: boolean | null;
  acknowledgedInput: number;
  finishedAt: number | null;
  rank: number | null;
}

export interface RoomPlayer {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  skin: string;
  isBot: boolean;
  connected: boolean;
  ready: boolean;
  spectator: boolean;
  /** Team 0/1 during team heats; null in free-for-all races. */
  team: number | null;
  race: PlayerRaceState;
}

export interface RaceResult {
  playerId: string;
  displayName: string;
  rank: number;
  finishMs: number;
  dropperScore: number;
  poolHit: boolean | null;
  stuntScore: number;
  totalScore: number;
}

export interface RoomState {
  protocolVersion: typeof PROTOCOL_VERSION;
  roomId: string;
  phase: RoomPhase;
  leaderId: string | null;
  settingsRevision: number;
  settings: GameSettings;
  resolvedPoolShape: Exclude<PoolShape, "random">;
  players: RoomPlayer[];
  serverTime: number;
  countdownStartedAt: number | null;
  countdownEndsAt: number | null;
  raceStartedAt: number | null;
  raceEndsAt: number | null;
  results: RaceResult[];
  round: number;
}

export interface PublicConfig {
  clientId: string;
  guestAllowed: boolean;
}

/**
 * POST /api/oauth/token JSON body.
 * `code` is the short-lived code returned by Discord's SDK authorize call.
 * In an explicitly guest-enabled development server it may be omitted and
 * `guestName` is used instead. `instanceId` is always required and is bound
 * cryptographically into the returned ticket.
 */
export interface TokenRequest {
  code?: string;
  instanceId: string;
  guildId?: string | null;
  guestName?: string;
  /** Opaque per-browser preview key; the server HMACs it into a stable guest ID. */
  guestKey?: string;
}

export interface TokenResponse {
  ticket: string;
  expiresAt: number;
  accessToken: string | null;
  instanceId: string;
  user: {
    id: string;
    displayName: string;
    /** Discord avatar hash, when Discord supplied one. */
    avatar: string | null;
    avatarUrl: string | null;
    guest: boolean;
  };
}

/** Socket.IO client `auth` object: `{ ticket, instanceId }`. */
export interface SocketAuth {
  ticket: string;
  instanceId: string;
}

export interface EventAck<T = undefined> {
  ok: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Client -> server events:
 * - `lobby:settings`: `{ revision, patch }`; leader only, lobby only. The
 *   revision must match `room:state.settingsRevision`.
 * - `lobby:ready`: `{ ready }`; human lobby players only.
 * - `race:start`: no payload (or `{}`); leader only and all racers ready.
 * - `race:input`: `{ seq, steer, forward, tuck, jump }`; accepted only while racing.
 *   First-party clients keep `forward` enabled so iframe focus loss cannot stop a rider.
 * - `race:rematch`: no payload (or `{}`); leader only in results.
 *
 * Each event accepts an optional Socket.IO acknowledgement callback returning
 * `EventAck`. Invalid events never mutate room state.
 */
export interface ClientToServerEvents {
  "lobby:settings": (
    payload: { revision: number; patch: GameSettingsPatch },
    ack?: (result: EventAck<{ revision: number }>) => void,
  ) => void;
  "lobby:ready": (
    payload: { ready: boolean; skin?: string },
    ack?: (result: EventAck) => void,
  ) => void;
  "race:start": (
    payload?: Record<string, never>,
    ack?: (result: EventAck) => void,
  ) => void;
  "race:input": (
    payload: { seq: number; steer: number; forward: boolean; tuck: boolean; jump: boolean },
    ack?: (result: EventAck) => void,
  ) => void;
  "race:rematch": (
    payload?: Record<string, never>,
    ack?: (result: EventAck) => void,
  ) => void;
}

/** Server -> client events. A full snapshot is sent on join and at 20 Hz. */
export interface ServerToClientEvents {
  "room:state": (state: RoomState) => void;
  "room:error": (error: { code: string; message: string }) => void;
}
